import { useState, useEffect, useRef } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, CartesianGrid, ReferenceLine, Legend
} from "recharts";

// ─── MOCK DATA ────────────────────────────────────────────────────────────────

const RECONCILIATION_DATA = [
  {
    id: "REC-2025-1228-001",
    operator: "Lucky Star Casino",
    period: "Dec 27, 2025",
    reportedRevenue: 420000,
    processorRevenue: 251800,
    discrepancy: 168200,
    discrepancyPct: 40.0,
    status: "CRITICAL",
    processor: "LIPAY",
    flagged: true,
    detail: "Operator reported LRD 420K; LIPAY transaction logs show only LRD 251.8K in verified deposits. Gap of LRD 168.2K (40%) exceeds 10% threshold.",
  },
  {
    id: "REC-2025-1228-002",
    operator: "DoxxBet Liberia",
    period: "Dec 27, 2025",
    reportedRevenue: 143000,
    processorRevenue: 128700,
    discrepancy: 14300,
    discrepancyPct: 10.0,
    status: "WARNING",
    processor: "MTN Mobile Money",
    flagged: true,
    detail: "Minor discrepancy detected. Likely timing difference in MTN settlement. Monitoring for 48 hours before escalation.",
  },
  {
    id: "REC-2025-1228-003",
    operator: "Bet360 Liberia",
    period: "Dec 27, 2025",
    reportedRevenue: 892000,
    processorRevenue: 887400,
    discrepancy: 4600,
    discrepancyPct: 0.5,
    status: "MATCHED",
    processor: "LIPAY + MTN",
    flagged: false,
    detail: "Within acceptable variance. Difference attributable to pending settlement batch.",
  },
  {
    id: "REC-2025-1228-004",
    operator: "Premier Lotto",
    period: "Dec 27, 2025",
    reportedRevenue: 1200000,
    processorRevenue: 1197000,
    discrepancy: 3000,
    discrepancyPct: 0.25,
    status: "MATCHED",
    processor: "Orange Money + LIPAY",
    flagged: false,
    detail: "Excellent match. Rounding difference only.",
  },
  {
    id: "REC-2025-1228-005",
    operator: "Monrovia Bet",
    period: "Dec 27, 2025",
    reportedRevenue: 670000,
    processorRevenue: 634200,
    discrepancy: 35800,
    discrepancyPct: 5.3,
    status: "WARNING",
    processor: "MTN Mobile Money",
    flagged: true,
    detail: "Discrepancy within elevated watch range. Possible batch timing gap from MTN weekend settlement delay.",
  },
  {
    id: "REC-2025-1228-006",
    operator: "Galaxy Gaming",
    period: "Dec 27, 2025",
    reportedRevenue: 0,
    processorRevenue: 0,
    discrepancy: 0,
    discrepancyPct: 0,
    status: "SUSPENDED",
    processor: "N/A",
    flagged: false,
    detail: "Operator license suspended. No transactions permitted.",
  },
];

const HOURLY_FLOW = [
  { hour: "00:00", lipay: 42000, mtn: 18000, orange: 9000 },
  { hour: "02:00", lipay: 18000, mtn: 7000, orange: 3000 },
  { hour: "04:00", lipay: 9000, mtn: 3000, orange: 1000 },
  { hour: "06:00", lipay: 14000, mtn: 5000, orange: 4000 },
  { hour: "08:00", lipay: 55000, mtn: 22000, orange: 14000 },
  { hour: "10:00", lipay: 88000, mtn: 36000, orange: 21000 },
  { hour: "12:00", lipay: 104000, mtn: 47000, orange: 28000 },
  { hour: "14:00", lipay: 97000, mtn: 44000, orange: 25000 },
  { hour: "16:00", lipay: 122000, mtn: 56000, orange: 32000 },
  { hour: "18:00", lipay: 186000, mtn: 78000, orange: 45000 },
  { hour: "20:00", lipay: 215000, mtn: 91000, orange: 54000 },
  { hour: "22:00", lipay: 178000, mtn: 74000, orange: 41000 },
];

const LIVE_TRANSACTIONS = [
  { id: "TXN-882941", time: "14:23:08", operator: "Bet360 Liberia", amount: 1500, type: "Deposit", processor: "LIPAY", status: "CLEARED", flag: null },
  { id: "TXN-882940", time: "14:22:55", operator: "Lucky Star Casino", amount: 85000, type: "Deposit", processor: "LIPAY", status: "FLAGGED", flag: "Unusual amount — 18× avg" },
  { id: "TXN-882939", time: "14:22:41", operator: "Premier Lotto", amount: 500, type: "Deposit", processor: "MTN", status: "CLEARED", flag: null },
  { id: "TXN-882938", time: "14:22:19", operator: "Monrovia Bet", amount: 3200, type: "Withdrawal", processor: "MTN", status: "WATCH", flag: "Weekend settlement pending" },
  { id: "TXN-882937", time: "14:21:58", operator: "DoxxBet Liberia", amount: 12000, type: "Deposit", processor: "Orange", status: "FLAGGED", flag: "Cross-border routing detected" },
  { id: "TXN-882936", time: "14:21:44", operator: "Bet360 Liberia", amount: 2800, type: "Deposit", processor: "LIPAY", status: "CLEARED", flag: null },
  { id: "TXN-882935", time: "14:21:33", operator: "Premier Lotto", amount: 1000, type: "Deposit", processor: "MTN", status: "CLEARED", flag: null },
  { id: "TXN-882934", time: "14:21:09", operator: "Lucky Star Casino", amount: 7500, type: "Withdrawal", processor: "LIPAY", status: "FLAGGED", flag: "Payout ratio anomaly" },
  { id: "TXN-882933", time: "14:20:51", operator: "Monrovia Bet", amount: 4500, type: "Deposit", processor: "MTN", status: "CLEARED", flag: null },
  { id: "TXN-882932", time: "14:20:34", operator: "Bet360 Liberia", amount: 900, type: "Deposit", processor: "MTN", status: "CLEARED", flag: null },
];

const PROCESSOR_HEALTH = [
  { name: "LIPAY", status: "OPERATIONAL", latency: "142ms", uptime: "99.2%", lastCheck: "14:23:01", color: "#52C41A" },
  { name: "MTN Mobile Money", status: "OPERATIONAL", latency: "89ms", uptime: "99.8%", lastCheck: "14:23:01", color: "#52C41A" },
  { name: "Orange Money", status: "DEGRADED", latency: "1,240ms", uptime: "97.1%", lastCheck: "14:23:01", color: "#FAAD14" },
];

// ─── HELPERS ──────────────────────────────────────────────────────────────────

const statusConfig = {
  CRITICAL: { color: "#FF4D4F", bg: "rgba(255,77,79,0.12)", border: "rgba(255,77,79,0.3)" },
  WARNING: { color: "#FAAD14", bg: "rgba(250,173,20,0.12)", border: "rgba(250,173,20,0.3)" },
  MATCHED: { color: "#52C41A", bg: "rgba(82,196,26,0.12)", border: "rgba(82,196,26,0.3)" },
  SUSPENDED: { color: "#595959", bg: "rgba(89,89,89,0.12)", border: "rgba(89,89,89,0.3)" },
};

const txStatusConfig = {
  CLEARED: { color: "#52C41A", bg: "rgba(82,196,26,0.1)" },
  FLAGGED: { color: "#FF4D4F", bg: "rgba(255,77,79,0.1)" },
  WATCH: { color: "#FAAD14", bg: "rgba(250,173,20,0.1)" },
};

function fmtLRD(n) {
  if (n >= 1000000) return `LRD ${(n / 1000000).toFixed(2)}M`;
  if (n >= 1000) return `LRD ${(n / 1000).toFixed(1)}K`;
  return `LRD ${n.toLocaleString()}`;
}

// ─── TICKER ───────────────────────────────────────────────────────────────────

function LiveTicker() {
  const [tick, setTick] = useState(0);
  const tickRef = useRef(0);

  useEffect(() => {
    const id = setInterval(() => {
      tickRef.current += 1;
      setTick(tickRef.current);
    }, 2800);
    return () => clearInterval(id);
  }, []);

  const idx = tick % LIVE_TRANSACTIONS.length;
  const tx = LIVE_TRANSACTIONS[idx];
  const cfg = txStatusConfig[tx.status];

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 12, padding: "10px 16px",
      background: "rgba(255,255,255,0.025)", borderRadius: 8,
      border: "1px solid rgba(255,255,255,0.06)", fontFamily: "'IBM Plex Mono', monospace",
      fontSize: 11, overflow: "hidden", marginBottom: 20,
    }}>
      <span style={{ color: "#52C41A", animation: "blink 1s step-end infinite" }}>●</span>
      <span style={{ color: "#595959" }}>LIVE</span>
      <span style={{ color: "#595959", margin: "0 4px" }}>|</span>
      <span style={{ color: "#40A9FF" }}>{tx.id}</span>
      <span style={{ color: "#595959" }}>{tx.time}</span>
      <span style={{ color: "#D9D9D9" }}>{tx.operator}</span>
      <span style={{ color: "#8C8C8C" }}>{tx.type}</span>
      <span style={{ color: "#F0F0F0", fontWeight: 700 }}>{fmtLRD(tx.amount)}</span>
      <span style={{ color: "#595959" }}>via {tx.processor}</span>
      <span style={{
        marginLeft: "auto", fontSize: 10, fontWeight: 700, letterSpacing: "0.06em",
        color: cfg.color, background: cfg.bg, padding: "2px 8px", borderRadius: 4,
      }}>{tx.status}</span>
      {tx.flag && <span style={{ fontSize: 10, color: "#FF7A00", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>⚠ {tx.flag}</span>}
      <style>{`@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.2} }`}</style>
    </div>
  );
}

// ─── RECON ROW ────────────────────────────────────────────────────────────────

function ReconRow({ rec, selected, onClick }) {
  const cfg = statusConfig[rec.status];
  const isSelected = selected?.id === rec.id;

  return (
    <div onClick={onClick} style={{
      display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 100px",
      gap: 12, padding: "12px 16px", cursor: "pointer",
      background: isSelected ? "rgba(64,169,255,0.06)" : rec.flagged ? "rgba(255,77,79,0.02)" : "transparent",
      border: isSelected ? "1px solid rgba(64,169,255,0.3)" : "1px solid transparent",
      borderRadius: 8, alignItems: "center",
      borderBottom: "1px solid rgba(255,255,255,0.05)",
      transition: "all 0.15s",
    }}>
      <div>
        <div style={{ fontSize: 12, fontWeight: 600, color: "#D9D9D9", fontFamily: "'IBM Plex Sans', sans-serif", marginBottom: 2 }}>{rec.operator}</div>
        <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>{rec.processor} · {rec.period}</div>
      </div>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#A8A8A8" }}>{fmtLRD(rec.reportedRevenue)}</div>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: "#A8A8A8" }}>{fmtLRD(rec.processorRevenue)}</div>
      <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: cfg.color, fontWeight: 700 }}>
        {rec.discrepancyPct > 0 ? `${rec.discrepancyPct.toFixed(1)}%` : "—"}
      </div>
      <span style={{
        fontSize: 10, fontWeight: 700, fontFamily: "'IBM Plex Mono', monospace",
        color: cfg.color, background: cfg.bg, border: `1px solid ${cfg.border}`,
        padding: "3px 10px", borderRadius: 4, textAlign: "center",
      }}>{rec.status}</span>
    </div>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

export default function NLA_TransactionMonitor() {
  const [selected, setSelected] = useState(RECONCILIATION_DATA[0]);
  const [subView, setSubView] = useState("reconciliation");

  const totalFlagged = RECONCILIATION_DATA.filter(r => r.flagged).length;
  const criticalCount = RECONCILIATION_DATA.filter(r => r.status === "CRITICAL").length;
  const totalVol = RECONCILIATION_DATA.reduce((s, r) => s + r.reportedRevenue, 0);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0D0D1A 0%, #0A1628 50%, #0D0D1A 100%)",
      fontFamily: "'IBM Plex Sans', 'Segoe UI', sans-serif",
      color: "#D9D9D9", padding: 24,
    }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;600;700&family=IBM+Plex+Sans:wght@400;500;600;700;800&display=swap'); *{box-sizing:border-box;} ::-webkit-scrollbar{width:6px;} ::-webkit-scrollbar-thumb{background:rgba(255,255,255,0.1);border-radius:3px;}`}</style>

      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 6, height: 28, background: "linear-gradient(180deg, #52C41A, #237804)", borderRadius: 3 }} />
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: "#F0F0F0", letterSpacing: "-0.02em" }}>
              Transaction Monitoring
            </h1>
          </div>
          <div style={{ fontSize: 11, color: "#595959", marginLeft: 16, fontFamily: "'IBM Plex Mono', monospace" }}>
            NLA · Payment Processor Cross-Validation · Dec 28, 2025 14:23 WAT
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {["reconciliation", "flow", "transactions"].map(v => (
            <button key={v} onClick={() => setSubView(v)} style={{
              background: subView === v ? "rgba(82,196,26,0.15)" : "rgba(255,255,255,0.04)",
              border: `1px solid ${subView === v ? "rgba(82,196,26,0.5)" : "rgba(255,255,255,0.08)"}`,
              color: subView === v ? "#52C41A" : "#8C8C8C", cursor: "pointer",
              padding: "7px 14px", borderRadius: 7, fontSize: 11, fontWeight: 600,
              fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.07em",
              transition: "all 0.2s",
            }}>
              {v === "reconciliation" ? "⇌ Reconciliation" : v === "flow" ? "∿ Payment Flow" : "≡ Live Feed"}
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
        {[
          { label: "Total Volume (Today)", value: fmtLRD(totalVol), color: "#40A9FF" },
          { label: "Operators Reconciled", value: `${RECONCILIATION_DATA.length - 1}/5`, color: "#52C41A", sub: "Galaxy suspended" },
          { label: "Flagged Discrepancies", value: totalFlagged, color: totalFlagged > 0 ? "#FAAD14" : "#52C41A" },
          { label: "Critical Gaps", value: criticalCount, color: criticalCount > 0 ? "#FF4D4F" : "#52C41A", sub: "≥10% variance" },
        ].map(item => (
          <div key={item.label} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ fontSize: 10, color: "#595959", marginBottom: 6, fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.07em" }}>{item.label}</div>
            <div style={{ fontSize: 28, fontWeight: 800, color: item.color, fontFamily: "'IBM Plex Mono', monospace", lineHeight: 1 }}>{item.value}</div>
            {item.sub && <div style={{ fontSize: 10, color: "#595959", marginTop: 4 }}>{item.sub}</div>}
          </div>
        ))}
      </div>

      {/* Processor Health Bar */}
      <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
        {PROCESSOR_HEALTH.map(p => (
          <div key={p.name} style={{
            flex: 1, background: "rgba(255,255,255,0.03)", border: `1px solid ${p.color}30`,
            borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10,
          }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.color, flexShrink: 0, boxShadow: `0 0 6px ${p.color}` }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: "#D9D9D9" }}>{p.name}</span>
            <span style={{ marginLeft: "auto", fontSize: 11, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>{p.latency}</span>
            <span style={{ fontSize: 10, color: p.color, fontFamily: "'IBM Plex Mono', monospace" }}>{p.status}</span>
          </div>
        ))}
      </div>

      {/* Sub Views */}
      {subView === "reconciliation" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 16 }}>
          <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr 100px", gap: 12, padding: "10px 16px", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
              {["Operator", "Reported", "Processor", "Gap", "Status"].map(h => (
                <div key={h} style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.07em" }}>{h}</div>
              ))}
            </div>
            <div style={{ padding: "8px" }}>
              {RECONCILIATION_DATA.map(rec => (
                <ReconRow key={rec.id} rec={rec} selected={selected} onClick={() => setSelected(rec)} />
              ))}
            </div>
          </div>

          {/* Detail */}
          {selected && (
            <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 20 }}>
              <div style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>{selected.id}</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#F0F0F0", marginBottom: 16 }}>{selected.operator}</div>

              <div style={{ marginBottom: 16 }}>
                {[
                  { label: "Reported Revenue", value: fmtLRD(selected.reportedRevenue), color: "#D9D9D9" },
                  { label: "Processor Verified", value: fmtLRD(selected.processorRevenue), color: "#D9D9D9" },
                  { label: "Discrepancy", value: fmtLRD(selected.discrepancy), color: statusConfig[selected.status].color },
                  { label: "Gap %", value: `${selected.discrepancyPct.toFixed(2)}%`, color: statusConfig[selected.status].color },
                  { label: "Processor", value: selected.processor, color: "#8C8C8C" },
                ].map(item => (
                  <div key={item.label} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
                    <span style={{ fontSize: 11, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>{item.label}</span>
                    <span style={{ fontSize: 12, fontWeight: 600, color: item.color, fontFamily: "'IBM Plex Mono', monospace" }}>{item.value}</span>
                  </div>
                ))}
              </div>

              <div style={{
                background: `${statusConfig[selected.status].bg}`,
                border: `1px solid ${statusConfig[selected.status].border}`,
                borderRadius: 8, padding: "12px 14px",
              }}>
                <div style={{ fontSize: 10, color: statusConfig[selected.status].color, fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, marginBottom: 6, textTransform: "uppercase" }}>
                  System Assessment
                </div>
                <div style={{ fontSize: 12, color: "#A8A8A8", lineHeight: 1.6 }}>{selected.detail}</div>
              </div>

              {selected.flagged && (
                <button style={{
                  marginTop: 14, width: "100%", padding: "10px", borderRadius: 8, border: "none", cursor: "pointer",
                  background: "rgba(255,77,79,0.15)", color: "#FF4D4F", fontFamily: "'IBM Plex Mono', monospace",
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                }}>
                  ↗ Escalate to FIA
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {subView === "flow" && (
        <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 24 }}>
          <div style={{ fontSize: 12, color: "#8C8C8C", marginBottom: 20 }}>24-Hour Payment Processor Flow — All Operators (LRD)</div>
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={HOURLY_FLOW} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#595959", fontFamily: "'IBM Plex Mono', monospace" }} axisLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#595959" }} axisLine={false} tickFormatter={v => `${(v / 1000).toFixed(0)}K`} />
              <Tooltip
                contentStyle={{ background: "#1A1A2E", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 6, fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}
                formatter={(v, name) => [fmtLRD(v), name]}
              />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", color: "#8C8C8C" }} />
              <Bar dataKey="lipay" name="LIPAY" fill="#40A9FF" radius={[2, 2, 0, 0]} />
              <Bar dataKey="mtn" name="MTN Mobile" fill="#52C41A" radius={[2, 2, 0, 0]} />
              <Bar dataKey="orange" name="Orange Money" fill="#FAAD14" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {subView === "transactions" && (
        <div style={{ background: "rgba(255,255,255,0.025)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, overflow: "hidden" }}>
          <LiveTicker />
          <div style={{ padding: "0 16px 8px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "110px 140px 2fr 1fr 80px 80px 1fr", gap: 12, padding: "8px 0", borderBottom: "1px solid rgba(255,255,255,0.06)", marginBottom: 4 }}>
              {["TXN ID", "Time", "Operator", "Amount", "Type", "Processor", "Status"].map(h => (
                <div key={h} style={{ fontSize: 10, color: "#595959", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase", letterSpacing: "0.07em" }}>{h}</div>
              ))}
            </div>
            {LIVE_TRANSACTIONS.map((tx, i) => {
              const cfg = txStatusConfig[tx.status];
              return (
                <div key={tx.id} style={{
                  display: "grid", gridTemplateColumns: "110px 140px 2fr 1fr 80px 80px 1fr",
                  gap: 12, padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)",
                  background: tx.status === "FLAGGED" ? "rgba(255,77,79,0.02)" : "transparent",
                }}>
                  <span style={{ fontSize: 11, color: "#40A9FF", fontFamily: "'IBM Plex Mono', monospace" }}>{tx.id}</span>
                  <span style={{ fontSize: 11, color: "#595959", fontFamily: "'IBM Plex Mono', monospace" }}>{tx.time}</span>
                  <span style={{ fontSize: 12, color: "#D9D9D9" }}>{tx.operator}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: "#F0F0F0", fontFamily: "'IBM Plex Mono', monospace" }}>{fmtLRD(tx.amount)}</span>
                  <span style={{ fontSize: 11, color: "#8C8C8C" }}>{tx.type}</span>
                  <span style={{ fontSize: 11, color: "#595959" }}>{tx.processor}</span>
                  <div>
                    <span style={{ fontSize: 10, fontWeight: 700, color: cfg.color, background: cfg.bg, padding: "2px 8px", borderRadius: 4, fontFamily: "'IBM Plex Mono', monospace" }}>
                      {tx.status}
                    </span>
                    {tx.flag && <div style={{ fontSize: 10, color: "#FF7A00", marginTop: 3 }}>⚠ {tx.flag}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div style={{ marginTop: 20, textAlign: "center", fontSize: 10, color: "#333", fontFamily: "'IBM Plex Mono', monospace" }}>
        GCRMS · XI CONSULT CORP · CONFIDENTIAL — NLA DEMO BUILD
      </div>
    </div>
  );
}